import type { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import {
  Alert,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import React from 'react';
import { useIsFeatureEnabled } from 'src/hooks/useIsFeatureEnabled';
import {
  CUSTOM_FLOW_FEATURE_FLAGS,
  LOCKED_FEATURE_TOOLTIP_COPY,
  QuestionFlow,
} from '../types';

interface Props {
  value: QuestionFlow;
  onChange: (flow: QuestionFlow) => void;
  disabled?: boolean;
}

export const QuestionFlowSegmentedTabs: React.FC<Props> = ({
  value,
  onChange,
  disabled,
}) => {
  const { tokens } = useTheme<LensTheme>();
  const dynamicQuestionsEnabled = useIsFeatureEnabled(
    CUSTOM_FLOW_FEATURE_FLAGS[0],
  );
  const coiFormEnabled = useIsFeatureEnabled(CUSTOM_FLOW_FEATURE_FLAGS[1]);
  const customEnabled = dynamicQuestionsEnabled || coiFormEnabled;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.semantic.spacing.desktop.gap[2].value,
      }}
    >
      <ToggleButtonGroup
        exclusive
        fullWidth
        value={value}
        onChange={(_e, next: QuestionFlow | null) => {
          if (next) onChange(next);
        }}
        data-testid="question-flow-tabs"
      >
        <ToggleButton
          value="default"
          data-testid="question-flow-default"
          sx={{ textTransform: 'none' }}
        >
          Default question flow
        </ToggleButton>
        <ToggleButton
          value="custom"
          data-testid="question-flow-custom"
          disabled={disabled || !customEnabled}
          sx={{ textTransform: 'none' }}
        >
          Custom question flow
        </ToggleButton>
      </ToggleButtonGroup>
      {!customEnabled && (
        <Alert severity="info" data-testid="custom-flow-not-available">
          <Typography
            sx={{
              fontWeight: tokens.semantic.fontWeight.emphasis.value,
              fontSize: '14px',
            }}
          >
            Custom question flow is not available
          </Typography>
          <Typography sx={{ fontSize: '12px' }}>
            {LOCKED_FEATURE_TOOLTIP_COPY}
          </Typography>
        </Alert>
      )}
    </Box>
  );
};
